/**
 * Judge.me widgets sync after AJAX product switches.
 * Public API: window.updateJudgeMeWidgets(productId)
 *
 * Keeps preview badge, featured carousel, and bottom review widget on the
 * same product reviews (carousel is built from the product review widget HTML).
 */
(() => {
  const BADGE_URL = 'https://api.judge.me/api/v1/widgets/preview_badge';
  const REVIEW_URL = 'https://api.judge.me/api/v1/widgets/product_review';
  const TOKEN = 'SJxKs4qreDipFT-s-crKitbKuAI';
  const CAROUSEL_SLIDES = 12;
  const DONE = 'jdgm--done-setup';
  const DONE_W = 'jdgm--done-setup-widget';

  const $j = () => window.jdgm?.$;
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => [...r.querySelectorAll(s)];

  const creds = () => {
    const c = window.judgeMeConfig || {};
    return {
      shop:
        c.shopDomain ||
        window.jdgmSettings?.shop_domain ||
        window.Shopify?.shop ||
        '',
      token:
        c.apiToken ||
        window.jdgmSettings?.api_token ||
        window.jdgmSettings?.token ||
        window.jdgm?.PUBLIC_TOKEN ||
        TOKEN,
    };
  };

  const apiUrl = (endpoint, shop, token, productId, extra = {}) => {
    const p = new URLSearchParams({
      shop_domain: shop,
      external_id: String(productId),
      api_token: token,
    });
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, String(v));
    }
    return `${endpoint}?${p}`;
  };

  const reviewEl = () =>
    qs('.jdgm-review-widget') || qs('#judgeme_product_reviews');

  /** Resolve the active product id for badge / carousel / review widget sync. */
  const resolveProductId = () => {
    const visibleBadge = qs('[data-product-key]:not([hidden]) .jdgm-preview-badge[data-id]');
    if (visibleBadge?.getAttribute('data-id')) return visibleBadge.getAttribute('data-id');

    const badge = qs('.jdgm-preview-badge[data-id]');
    if (badge?.getAttribute('data-id')) return badge.getAttribute('data-id');

    const widget = reviewEl();
    if (widget?.getAttribute('data-id')) return widget.getAttribute('data-id');

    const metaId =
      window.meta?.product?.id ||
      window.ShopifyAnalytics?.meta?.product?.id ||
      null;
    return metaId != null ? String(metaId) : '';
  };

  const productHandle = () => {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  };

  const productImg = () => {
    const img = qs(
      [
        '.combined-product__media-panel:not([hidden]) .product__media img',
        '.combined-product__media-panel:not([hidden]) img',
        '[data-product-key]:not([hidden]) .product__media img',
        '.gift-set-product .product__media img',
        '.product__media-item.is-active img',
        '.product__media img',
        'media-gallery img',
        '.product .product__media img',
      ].join(', ')
    );
    return img?.currentSrc || img?.src || '';
  };

  const dateParts = (raw) => {
    if (!raw) return null;
    const v = String(raw).trim();
    if (!v) return null;
    let m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) return { mm: m[1].padStart(2, '0'), dd: m[2].padStart(2, '0'), yyyy: m[3] };
    m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { yyyy: m[1], mm: m[2], dd: m[3] };
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return {
      yyyy: String(d.getFullYear()),
      mm: String(d.getMonth() + 1).padStart(2, '0'),
      dd: String(d.getDate()).padStart(2, '0'),
    };
  };

  const dateText = (parts) => {
    if (!parts) return '';
    const fmt =
      window.jdgmSettings?.review_date_format ||
      window.jdgmSettings?.featured_carousel_date_format ||
      'dd/mm/yyyy';
    if (!fmt || fmt === 'timestamp') return `${parts.dd}/${parts.mm}/${parts.yyyy}`;
    return String(fmt)
      .replaceAll('yyyy', parts.yyyy)
      .replaceAll('yy', parts.yyyy.slice(2))
      .replaceAll('mm', parts.mm)
      .replaceAll('dd', parts.dd);
  };

  const htmlFromJson = (data, keys) => {
    if (!data || typeof data === 'string') return data || '';
    for (const k of keys) if (data[k]) return data[k];
    return data.html || data.widget || data.badge || '';
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const parseResponse = async (res, keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    const raw = await res.text();
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json') && !raw.trim().startsWith('{')) return raw;
    try {
      const html = htmlFromJson(JSON.parse(raw), list);
      if (!html) throw new Error(`Judge.me missing HTML (${list.join(', ')})`);
      return html;
    } catch (e) {
      if (e instanceof SyntaxError) return raw;
      throw e;
    }
  };

  const reviewMeta = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = String(html || '');
    const root = qs('.jdgm-rev-widg', temp) || qs('.jdgm-review-widget', temp) || temp;
    const score =
      root.getAttribute('data-average-rating') ||
      qs('.jdgm-rev-widg', temp)?.getAttribute('data-average-rating') ||
      qs('.jdgm-rev-widg__summary-average, .jdgm-all-reviews-rating, [data-score]', temp)?.getAttribute('data-score') ||
      '';
    const countAttr =
      root.getAttribute('data-number-of-reviews') ||
      qs('.jdgm-rev-widg', temp)?.getAttribute('data-number-of-reviews') ||
      '';
    const reviews = qsa('.jdgm-rev', temp).map((rev) => {
      const rating = qs('.jdgm-rev__rating', rev);
      const time = qs('.jdgm-rev__timestamp', rev);
      return {
        id: rev.getAttribute('data-review-id') || rev.getAttribute('data-reviewid') || rev.id || '',
        author: qs('.jdgm-rev__author', rev)?.textContent?.trim() || '',
        title: qs('.jdgm-rev__title', rev)?.textContent?.trim() || '',
        body: qs('.jdgm-rev__body', rev)?.innerHTML || '',
        score: rating?.getAttribute('data-score') || '',
        ratingHtml: rating?.innerHTML || '',
        timestamp:
          time?.getAttribute('data-content') ||
          time?.getAttribute('data-time') ||
          time?.textContent?.trim() ||
          '',
      };
    });
    return { score: String(score || ''), count: String(countAttr || reviews.length || '0'), reviews };
  };

  const callJdgm = (name, ...args) => {
    const fn = window.jdgm?.[name];
    if (typeof fn === 'function') fn(...args);
  };

  class JudgeMeWidgetsSync extends HTMLElement {
    #abort = null;
    #lock = 0;
    #y = 0;
    #scrollTo = null;

    async update(productId) {
      if (productId == null || productId === '') {
        console.warn('[Judge.me] updateJudgeMeWidgets called without a productId');
        return;
      }
      const { shop, token } = creds();
      if (!shop) {
        console.error('[Judge.me] Missing shop_domain.');
        return;
      }
      if (!token) {
        console.error('[Judge.me] Missing api_token.');
        return;
      }

      this.#abort?.abort();
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      const id = String(productId);
      this.#lockScroll();

      try {
        const [cached, api] = await Promise.all([
          this.#cache(id, signal),
          this.#api(id, signal),
        ]);
        // Badge + bottom widget use the default product widget.
        // Carousel uses the same product with a higher per_page so all reviews
        // appear as slides (default widget HTML only includes the first page ~3).
        const badge = cached?.badge || api.badge;
        const widget = cached?.widget || api.widget;
        const carousel = api.carouselWidget || widget;

        this.#restore();
        this.#setBadge(id, badge);
        this.#setWidget(id, widget);
        const ok = this.#buildCarousel(id, carousel, reviewMeta(widget));
        this.#restore();
        this.#rebindWidget(id);
        if (ok) this.#rebindCarousel();
        this.#restore();
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('[Judge.me] Failed to update widgets:', e);
      } finally {
        this.#unlockScroll();
      }
    }

    #scrollY() {
      return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    #restore() {
      window.scrollTo(0, this.#y);
      document.documentElement.scrollTop = this.#y;
      document.body.scrollTop = this.#y;
    }

    #lockScroll() {
      if (this.#lock === 0) {
        this.#y = this.#scrollY();
        if (typeof window.jdgm?.scrollTo === 'function') {
          this.#scrollTo = window.jdgm.scrollTo;
          window.jdgm.scrollTo = () => {};
        }
      }
      this.#lock += 1;
      this.#restore();
    }

    #unlockScroll() {
      this.#lock = Math.max(0, this.#lock - 1);
      this.#restore();
      setTimeout(() => {
        this.#restore();
        if (this.#lock === 0) {
          if (this.#scrollTo && window.jdgm) {
            window.jdgm.scrollTo = this.#scrollTo;
            this.#scrollTo = null;
          }
          this.#restore();
        }
      }, 100);
      requestAnimationFrame(() => this.#restore());
    }

    async #cache(productId, signal) {
      try {
        const jdgm = window.jdgm || {};
        const { shop, token } = creds();
        const domain = jdgm.SHOP_DOMAIN || shop;
        const publicToken = jdgm.PUBLIC_TOKEN || token;
        if (!domain || !publicToken) return null;
        const p = new URLSearchParams({
          public_token: publicToken,
          preview_badge_product_ids: String(productId),
          review_widget_product_ids: String(productId),
        });
        const res = await fetch(
          `${jdgm.CACHE_SERVER_HOST || 'https://cache.judge.me'}/widgets/${jdgm.PLATFORM || 'shopify'}/${domain}?${p}`,
          { signal }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const id = String(productId);
        const badge = data.preview_badges?.[id] || data.preview_badges?.[productId] || null;
        const widget = data.review_widgets?.[id] || data.review_widgets?.[productId] || null;
        return badge || widget ? { badge, widget } : null;
      } catch {
        return null;
      }
    }

    async #api(productId, signal) {
      const { shop, token } = creds();
      const urls = [
        apiUrl(BADGE_URL, shop, token, productId),
        apiUrl(REVIEW_URL, shop, token, productId),
        // Same product as the bottom widget; higher per_page so carousel gets all reviews.
        apiUrl(REVIEW_URL, shop, token, productId, { per_page: CAROUSEL_SLIDES, page: 1 }),
      ];
      const [badgeRes, reviewRes, carouselRes] = await Promise.all(
        urls.map((u) => fetch(u, { signal }))
      );
      if (!badgeRes.ok) throw new Error(`Preview Badge failed (${badgeRes.status})`);
      if (!reviewRes.ok) throw new Error(`Review Widget failed (${reviewRes.status})`);

      const [badge, widget] = await Promise.all([
        parseResponse(badgeRes, ['badge']),
        parseResponse(reviewRes, ['widget']),
      ]);

      let carouselWidget = widget;
      if (carouselRes.ok) {
        try {
          carouselWidget = await parseResponse(carouselRes, ['widget']);
        } catch {
          carouselWidget = widget;
        }
      }

      // If the shop's page size still limits HTML to ~3 reviews, pull extra pages
      // so the carousel can show every review for this product.
      carouselWidget = await this.#expandCarouselReviews(
        productId,
        shop,
        token,
        signal,
        widget,
        carouselWidget
      );

      return { badge, widget, carouselWidget };
    }

    async #expandCarouselReviews(productId, shop, token, signal, widgetHtml, carouselHtml) {
      const baseMeta = reviewMeta(widgetHtml);
      const carouselMeta = reviewMeta(carouselHtml);
      const total = parseInt(baseMeta.count || carouselMeta.count || '0', 10) || 0;
      const have = carouselMeta.reviews.length;

      if (!total || have >= total || have >= CAROUSEL_SLIDES) {
        return carouselHtml;
      }

      const perPage = Math.max(have, 1);
      const maxPage = Math.min(Math.ceil(total / perPage), 8);
      const byId = new Map();
      for (const rev of carouselMeta.reviews) {
        byId.set(rev.id || `${rev.author}|${rev.title}|${rev.body}`, rev);
      }

      for (let page = 2; page <= maxPage && byId.size < total && byId.size < CAROUSEL_SLIDES; page += 1) {
        try {
          const res = await fetch(
            apiUrl(REVIEW_URL, shop, token, productId, { per_page: perPage, page }),
            { signal }
          );
          if (!res.ok) break;
          const html = await parseResponse(res, ['widget']);
          const pageMeta = reviewMeta(html);
          if (!pageMeta.reviews.length) break;
          for (const rev of pageMeta.reviews) {
            const key = rev.id || `${rev.author}|${rev.title}|${rev.body}`;
            if (!byId.has(key)) byId.set(key, rev);
          }
        } catch (e) {
          if (e.name === 'AbortError') throw e;
          break;
        }
      }

      if (byId.size <= have) return carouselHtml;

      // Rebuild a minimal review list HTML so #buildCarousel / reviewMeta can parse it.
      const merged = [...byId.values()].slice(0, CAROUSEL_SLIDES);
      const score = carouselMeta.score || baseMeta.score || '';
      const count = String(total || baseMeta.count || merged.length);
      const reviewsHtml = merged
        .map(
          (r) => `
          <div class="jdgm-rev" data-review-id="${String(r.id || '').replace(/"/g, '')}">
            <div class="jdgm-rev__author">${escapeHtml(r.author)}</div>
            <div class="jdgm-rev__title">${escapeHtml(r.title)}</div>
            <div class="jdgm-rev__body">${r.body || ''}</div>
            <div class="jdgm-rev__rating" data-score="${escapeHtml(r.score)}">${r.ratingHtml || ''}</div>
            <span class="jdgm-rev__timestamp" data-content="${escapeHtml(r.timestamp)}" data-time="${escapeHtml(r.timestamp)}"></span>
          </div>`
        )
        .join('');

      return `<div class="jdgm-rev-widg" data-average-rating="${escapeHtml(score)}" data-number-of-reviews="${escapeHtml(count)}">${reviewsHtml}</div>`;
    }

    #reveal(el) {
      if (!el) return;
      qsa('style.jdgm-temp-hiding-style', el).forEach((n) => n.remove());
      qsa('.jdgm-temp-hidden', el).forEach((n) => n.classList.remove('jdgm-temp-hidden'));
      qsa('.jdgm-prev-badge', el).forEach((n) => {
        if (n.style.display === 'none') n.style.display = '';
        const s = n.getAttribute('style');
        if (s === 'display:none' || s === 'display: none') n.removeAttribute('style');
      });
      qsa('.jdgm-rev-widg', el).forEach((n) => {
        if (n.style.display === 'none') n.style.display = '';
      });
      qsa('.jdgm-write-rev-link', el).forEach((n) => {
        n.style.removeProperty('display');
        if (n.getAttribute('style') === '') n.removeAttribute('style');
        n.hidden = false;
      });
      qsa('.jdgm-histogram', el).forEach((n) => {
        n.style.removeProperty('display');
        n.classList.remove('jdgm-temp-hidden');
      });
    }

    #syncId(el, productId) {
      if (!el || productId == null) return;
      const id = String(productId);
      el.setAttribute('data-id', id);
      const $ = $j();
      if (!$) return;
      const $el = $(el);
      $el.removeData('id');
      $el.attr('data-id', id);
      $el.data('id', id);
    }

    #clearSetup(el) {
      if (!el) return;
      el.classList.remove(DONE, DONE_W);
      qsa(`.${DONE}, .${DONE_W}`, el).forEach((n) => n.classList.remove(DONE, DONE_W));
      const $ = $j();
      if (!$) return;
      const $el = $(el);
      try {
        $el.off();
        $el.removeData();
      } catch { /* ignore */ }
      $el.removeClass(`${DONE} ${DONE_W}`);
      $el.find('.jdgm-rev, .jdgm-quest, .jdgm-ans').removeClass(DONE);
    }

    #setBadge(productId, html) {
      if (!html) return;
      let targets = qsa(`.jdgm-preview-badge[data-id="${productId}"]`);
      if (!targets.length) {
        const visible = qs('[data-product-key]:not([hidden]) .jdgm-preview-badge');
        const fallback = qs('.jdgm-preview-badge');
        targets = visible ? [visible] : fallback ? [fallback] : [];
      }
      for (const el of targets) {
        el.innerHTML = html;
        this.#syncId(el, productId);
        this.#reveal(el);
      }
    }

    #setWidget(productId, html) {
      if (!html) return;
      const widget = reviewEl();
      if (!widget) return;
      this.#clearSetup(widget);
      widget.innerHTML = html;
      this.#syncId(widget, productId);
      this.#reveal(widget);
      const $ = $j();
      if ($) $(widget).data('not-scroll-to', true);
    }

    #rebindWidget(productId) {
      try {
        if (!window.jdgm) return;
        const widget = reviewEl();
        qsa('.jdgm-preview-badge').forEach((el) => el.classList.remove(DONE, DONE_W));
        if (widget) {
          this.#syncId(widget, productId);
          this.#reveal(widget);
        }
        const $ = window.jdgm.$;
        if ($ && widget) {
          const $w = $(widget);
          this.#syncId(widget, productId);
          $w.data('not-scroll-to', true);
          $w.removeData('started-setup-reviews');
          $w.removeData('started-setup-questions');
          $w.removeClass(`${DONE} ${DONE_W}`);
          $w.find('.jdgm-rev, .jdgm-quest, .jdgm-ans').removeClass(DONE);

          [
            '_addEmptyWidgetHeader',
            '_customizeReviewWidget',
            'setupMediaGallery',
            '_renderAndSetupReviewForm',
            '_setupQuestionsForm',
            '_setupLoadReviewsEventsFor',
            '_setupFormsSubmit',
          ].forEach((fn) => callJdgm(fn, $w));
          callJdgm('toggleWriteReviewLink', $w, false);

          this.#syncId(widget, productId);
          $w.data('not-scroll-to', true);
          $w.addClass(window.jdgm.DONE_SETUP_WIDGET_CLASS || DONE_W);
          this.#reveal(widget);
        }
        callJdgm('customizeReviews');
        callJdgm('customizeBadges');
        this.#restore();
      } catch (e) {
        console.warn('[Judge.me] Widget rebind skipped:', e);
      }
    }

    #clearCarousel(wrapper) {
      const $ = $j();
      if (!$ || !wrapper) return;
      $(wrapper)
        .find('.jdgm-carousel')
        .each(function () {
          const $c = $(this);
          const t = $c.data('auto-slider');
          if (t) clearInterval(t);
          ['auto-slider', 'current-slide', 'slides-count', 'slides-per-view', 'slide-dimension'].forEach(
            (k) => $c.removeData(k)
          );
          $c.off('mouseover mouseout click');
          $c.find('.jdgm-carousel__right-arrow, .jdgm-carousel__left-arrow').off('click');
          $c.removeClass('jdgm-carousel--done');
        });
    }

    #stars(el, score) {
      if (!el || score === '' || score == null) return;
      el.setAttribute('data-score', String(score));
      el.setAttribute('aria-label', `Rating: ${score} stars`);
      if (window.jdgm?.$ && typeof window.jdgm.buildStarsFor === 'function') {
        const $el = window.jdgm.$(el);
        $el.empty();
        window.jdgm.buildStarsFor($el, score);
        return;
      }
      const v = Math.round(parseFloat(score) * 2) / 2;
      el.innerHTML = [1, 2, 3, 4, 5]
        .map((i) => {
          const state = v >= i ? 'on' : v >= i - 0.5 ? 'half' : 'off';
          return `<span class="jdgm-star jdgm--${state}"></span>`;
        })
        .join('');
    }

    #timestamp(el, raw) {
      if (!el) return;
      const parts = dateParts(raw);
      if (!parts) {
        el.classList.remove('jdgm-spinner');
        el.textContent = '';
        return;
      }
      const mmddyyyy = `${parts.mm}/${parts.dd}/${parts.yyyy}`;
      const yyyymmdd = `${parts.yyyy}-${parts.mm}-${parts.dd}`;
      const display = dateText(parts);
      el.setAttribute('data-time', mmddyyyy);
      el.setAttribute('data-content', yyyymmdd);
      el.classList.add('jdgm-spinner');
      el.textContent = '';

      const $ = $j();
      if ($) {
        const $el = $(el);
        $el.removeData('time');
        $el.removeData('content');
        $el.data('time', mmddyyyy);
        $el.data('content', yyyymmdd);
      }
      try {
        if (window.jdgm?.$ && typeof window.jdgm.customizeCarouselTimestamp === 'function') {
          window.jdgm.customizeCarouselTimestamp(window.jdgm.$(el).closest('.jdgm-carousel-item'));
        }
      } catch { /* fall through */ }

      if (el.classList.contains('jdgm-spinner') || !el.textContent.trim()) {
        el.textContent = display;
        el.classList.remove('jdgm-spinner');
      }
    }

    #carouselHeader(wrapper, score, count) {
      if (!wrapper) return;
      const rating = qs('.jdgm-all-reviews-rating', wrapper);
      if (rating && score !== '') this.#stars(rating, score);
      const tpl =
        window.jdgmSettings?.featured_carousel_count_text || 'from {{ n }} reviews';
      const text = String(tpl).replace(/\{\{\s*n\s*\}\}/g, String(count));
      qsa(
        '.jdgm-carousel-number-of-reviews, .jdgm-all-reviews-count, [data-number-of-reviews]',
        wrapper
      ).forEach((el) => {
        el.setAttribute('data-number-of-reviews', String(count));
        if (!el.childElementCount) {
          el.textContent = text;
          return;
        }
        const node = [...el.childNodes].find(
          (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
        );
        if (node) node.textContent = text;
        else el.insertAdjacentText('afterbegin', `${text} `);
      });
    }

    #fillSlide(item, review, meta) {
      if (!item || !review) return;
      if (review.id) item.setAttribute('data-review-id', String(review.id));

      const name = qs('.jdgm-carousel-item__reviewer-name', item);
      if (name) name.textContent = review.author || '';

      const title = qs('.jdgm-carousel-item__review-title', item);
      if (title) {
        title.textContent = review.title || '';
        title.classList.remove('jdgm-ellipsis');
      }

      const body = qs('.jdgm-carousel-item__review-body', item);
      if (body) {
        body.innerHTML = review.body || '';
        body.classList.remove('jdgm-ellipsis', 'jdgm-line-clamp');
        ['height', 'overflow', 'line-clamp', '-webkit-line-clamp'].forEach((p) =>
          body.style.removeProperty(p)
        );
      }

      const rating = qs('.jdgm-carousel-item__review-rating', item);
      if (rating) {
        if (review.ratingHtml) {
          rating.innerHTML = review.ratingHtml;
          if (review.score) rating.setAttribute('data-score', String(review.score));
        } else {
          this.#stars(rating, review.score || 0);
        }
      }

      const time = qs('.jdgm-carousel-item__timestamp', item);
      if (time) this.#timestamp(time, review.timestamp);

      if (meta.url || meta.handle) {
        const href = meta.url || `/products/${encodeURIComponent(meta.handle)}`;
        qsa('a[href*="/products/"]', item).forEach((a) => a.setAttribute('href', href));
      }
      if (meta.imageSrc) {
        qsa('.jdgm-carousel-item__product-image', item).forEach((img) => {
          const t = img.tagName === 'IMG' ? img : qs('img', img);
          if (!t) return;
          t.setAttribute('src', meta.imageSrc);
          t.setAttribute('srcset', '');
        });
      }
    }

    #buildCarousel(productId, html, countMeta = null) {
      const wrappers = qsa('.jdgm-carousel-wrapper');
      if (!wrappers.length || !html) return false;
      const meta = reviewMeta(html);
      if (!meta.reviews.length) return false;

      // Prefer total count from the default product widget (matches badge / bottom).
      const totalCount =
        (countMeta && countMeta.count) || meta.count || String(meta.reviews.length);

      const handle = productHandle();
      const product = {
        handle,
        url: handle ? `/products/${encodeURIComponent(handle)}` : '',
        imageSrc: productImg(),
      };
      const reviews = meta.reviews.slice(0, CAROUSEL_SLIDES);

      for (const wrapper of wrappers) {
        this.#clearCarousel(wrapper);
        this.#clearSetup(wrapper);
        const track = qs('.jdgm-carousel__item-wrapper', wrapper);
        const tplItem = qs('.jdgm-carousel-item', wrapper);
        if (!track || !tplItem) continue;

        const tpl = tplItem.cloneNode(true);
        qsa('.jdgm-carousel-item__full-review-link', tpl).forEach((n) => n.remove());
        track.innerHTML = '';
        for (const review of reviews) {
          const item = tpl.cloneNode(true);
          this.#fillSlide(item, review, product);
          track.appendChild(item);
        }
        track.style.transform = '';
        track.style.webkitTransform = '';
        this.#carouselHeader(wrapper, meta.score || countMeta?.score || '', totalCount);
        this.#syncId(wrapper, productId);
        qsa('.jdgm-carousel', wrapper).forEach((el) => el.classList.remove('jdgm-carousel--done'));
        this.#reveal(wrapper);
      }
      return true;
    }

    #rebindCarousel() {
      try {
        const jdgm = window.jdgm;
        if (!jdgm) return;
        qsa('.jdgm-carousel').forEach((el) => el.classList.remove('jdgm-carousel--done', DONE));
        if (typeof jdgm.initializeCarousel === 'function') jdgm.initializeCarousel();
        else if (jdgm.$ && typeof jdgm.updateCarousel === 'function') {
          jdgm.$('.jdgm-carousel').each(function () {
            jdgm.updateCarousel(jdgm.$(this));
          });
        }
        if (typeof jdgm.customizeCarouselTimestamp === 'function' && jdgm.$) {
          jdgm.customizeCarouselTimestamp(jdgm.$('.jdgm-carousel-item'));
        }
        qsa('.jdgm-carousel-item__timestamp.jdgm-spinner').forEach((el) => {
          const parts = dateParts(el.getAttribute('data-time') || el.getAttribute('data-content'));
          if (!parts) {
            el.classList.remove('jdgm-spinner');
            return;
          }
          el.textContent = dateText(parts);
          el.classList.remove('jdgm-spinner');
        });
      } catch (e) {
        console.warn('[Judge.me] Carousel rebind skipped:', e);
      }
    }
  }

  if (!customElements.get('judge-me-widgets-sync')) {
    customElements.define('judge-me-widgets-sync', JudgeMeWidgetsSync);
  }

  const sync = document.createElement('judge-me-widgets-sync');
  window.updateJudgeMeWidgets = (productId) => sync.update(productId);

  /**
   * Single-product PDPs (e.g. gift-set) never call updateJudgeMeWidgets from
   * product-combined / product-trial. Sync so the featured carousel uses the
   * same product reviews as the title badge and bottom widget.
   */
  const isCombinedOrTrialPdp = () => !!qs('product-combined, product-trial');

  const autoSyncSingleProduct = () => {
    if (isCombinedOrTrialPdp()) return;
    if (!qs('.jdgm-carousel-wrapper')) return;
    const id = resolveProductId();
    if (!id) return;
    sync.update(id);
  };

  const scheduleAutoSync = () => {
    if (isCombinedOrTrialPdp()) return;

    autoSyncSingleProduct();

    // Judge.me may init the store-wide featured carousel after our first pass —
    // re-apply product reviews once jdgm is ready so badge / slider / bottom match.
    let tries = 0;
    const retry = setInterval(() => {
      tries += 1;
      if (isCombinedOrTrialPdp() || tries > 40) {
        clearInterval(retry);
        return;
      }
      if (window.jdgm?.$) {
        clearInterval(retry);
        autoSyncSingleProduct();
      }
    }, 300);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAutoSync);
  } else {
    scheduleAutoSync();
  }
})();
